import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="10992" className="flex gap-6">
        {[1, 2, 3].map(i => <div key={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-semibold tracking-tight text-foreground/80 transition-all duration-300 hover:text-primary relative py-1 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-32 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-3xl font-bold tracking-tighter text-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground ${isScrolled ? "bg-white/80 backdrop-blur-xl border-b border-primary/10 shadow-xl shadow-primary/5" : "bg-white border-b border-transparent"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        
        <Link to="/HomePage" className="flex-shrink-0 z-50">
          <img src="https://direct-impala.10web.cloud/wp-content/uploads/2026/03/Transparent-Background-Logo.png" alt="Elyon Maintenance Incorporation" className="object-contain !w-[300px] !h-[170px]" />
        </Link>

        <div className="hidden md:block">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <Button asChild className="rounded-lg px-8 bg-primary hover:bg-primary/90 text-white border-0 shadow-lg shadow-primary/20 transition-all duration-300 font-bold tracking-tight">
            <Link to="https://elyonmaintenance.com/#get-your-maintenance-quote">
              Get a Free Quote
            </Link>
          </Button>
        </div>

        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent z-50" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-7" /> : <Menu className="size-7" />}
        </Button>
      </div>

      <div className={`fixed inset-0 bg-background/98 backdrop-blur-2xl z-40 md:hidden transition-all duration-500 flex flex-col pt-24 px-8 ${isMobileMenuOpen ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-8"}`}>
        <MenuProvider menu_id="16">
          <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>

        <div className="mt-12 flex flex-col gap-6 p-4 border-t border-border/40">
          <Button asChild size="lg" className="w-full rounded-lg bg-primary text-white border-0 py-7 text-lg font-bold shadow-xl shadow-primary/20">
            <Link to="#" onClick={() => setIsMobileMenuOpen(false)}>
              Get a Free Quote
            </Link>
          </Button>
          
          <div className="flex items-center justify-center gap-3 text-muted-foreground mt-6">
            <Phone className="size-6 text-primary" />
            <span className="text-xl font-bold text-foreground tracking-tighter">(682) 407-5903</span>
          </div>
        </div>
      </div>
    </header>;
}